/* =============================================================
   Raihan Saffron — motion.js
   CSS-first cinematic layer. No GSAP/Lenis/Three.js.
   - IntersectionObserver scroll reveals (once, threshold .15)
   - Magnetic primary CTA (desktop, fine pointer only)
   - Hero parallax drift + scroll-driven bloom crossfade
   All effects are skipped under prefers-reduced-motion / coarse pointer.
   ============================================================= */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer: fine)').matches;
  var desktop = window.matchMedia('(min-width: 990px)').matches;

  // Flag the page as motion-enabled so CSS can take over hiding/animating.
  // (Without this class, no-JS visitors see all content normally.)
  root.classList.add('rs-on');

  if (!('IntersectionObserver' in window)) {
    // No IntersectionObserver: rs-on would otherwise leave reveal targets hidden.
    // Reveal everything immediately so no content is ever stuck invisible.
    document.querySelectorAll('.rs-reveal, .rs-reveal-mask, [data-hero-seq]')
      .forEach(function (el) { el.classList.add('is-visible'); });
    document.querySelectorAll('main .shopify-section')
      .forEach(function (s) { s.classList.add('is-visible'); });
    return;
  }

  /* ---------- Scroll reveals ---------- */
  // Auto-target editorial elements + anything explicitly marked [data-rs].
  var SELECTORS = [
    '[data-rs]',
    '.rs-reveal',
    '.rs-reveal-mask',
    '.section-head',
    '.product-card',
    '.why-saffron__card',
    '.how-to-use__steps li',
    '.trust-strip__item',
    '.review-card',
    '.recipe-card',
    '.edu-card',
    '.gift__content'
  ].join(',');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      io.unobserve(entry.target);
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

  document.querySelectorAll(SELECTORS).forEach(function (el) {
    // Skip the hero block — it choreographs itself on load (see below).
    if (el.closest('.hero')) return;
    if (!el.classList.contains('rs-reveal') && !el.classList.contains('rs-reveal-mask')) {
      el.classList.add('rs-reveal');
    }
    // Stagger siblings within a shared parent for the editorial cascade.
    var parent = el.parentElement;
    if (parent) {
      var sibs = Array.prototype.indexOf.call(parent.children, el);
      el.style.setProperty('--rs-delay', Math.min(sibs, 4) * 110 + 'ms');
    }
    io.observe(el);
  });

  /* ---------- Section reveal flag (drives brass-thread dividers) ---------- */
  var sectionIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        sectionIO.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08 });
  document.querySelectorAll('main .shopify-section').forEach(function (s) {
    sectionIO.observe(s);
  });

  /* ---------- Hero on-load choreography (two-wave, film-synced) ----------
     When the "Pour & Bloom" film will play, the copy syncs to its beats:
     wave 1 (eyebrow ► headline ► sub) enters as the tea pours; wave 2
     (CTA ► facts) lands exactly on the gold-bloom payoff (~2.7s). If the
     film can't start within 2.6s (slow network, session-skip, custom image),
     the standard cascade runs so the headline is never held hostage. */
  var hero = document.querySelector('[data-cine-hero], .hero');
  if (hero) {
    var heroLines = hero.querySelectorAll('[data-hero-line]');
    var heroSeq = hero.querySelectorAll('[data-hero-seq]');
    var cascade = function (seqDelays, lineDelays) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          heroSeq.forEach(function (el, i) {
            el.style.setProperty('--rs-delay', (seqDelays[i] != null ? seqDelays[i] : seqDelays[seqDelays.length - 1]) + 'ms');
            el.classList.add('is-visible');
          });
          heroLines.forEach(function (el, i) {
            el.style.setProperty('--rs-delay', (lineDelays[i] != null ? lineDelays[i] : lineDelays[lineDelays.length - 1]) + 'ms');
            el.classList.add('is-visible');
          });
        });
      });
    };
    if (reduce) {
      heroLines.forEach(function (el) { el.classList.add('is-visible'); });
      heroSeq.forEach(function (el) { el.classList.add('is-visible'); });
    } else if (hero.classList.contains('cine-hero--seq') && !window.__rsCineSkip) {
      var played = false;
      var fallbackT = setTimeout(function () {
        if (played) return; played = true;
        cascade([0, 350, 550, 700], [100, 220]);            // standard cascade
      }, 300);
      document.addEventListener('cine:start', function () {
        if (played) return; played = true;
        clearTimeout(fallbackT);
        // seq order: eyebrow, sub, CTA, facts · lines: 1, 2
        cascade([0, 200, 350, 500], [80, 180]);           // film-synced waves (CTA on the froth-crown payoff)
      }, { once: true });
    } else {
      cascade([0, 350, 550, 700], [100, 220]);
    }
  }

  /* ---------- Count-up numbers (runs even under reduced motion: sets final) ---------- */
  var countEls = document.querySelectorAll('[data-count]');
  if (countEls.length) {
    var countIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        /* 2026-10-15 mobile pass (CHR-15): keep the decimals of the value (the lab figure 296.5 showed as 296);
           a value that is not a plain number keeps the text the server printed. */
        var el = entry.target, raw = String(el.getAttribute('data-count') || '').trim();
        countIO.unobserve(el);
        if (!/^\d+(\.\d+)?$/.test(raw)) return;
        var target = parseFloat(raw), dec = (raw.split('.')[1] || '').length;
        var fmt = function (v) { return dec ? v.toFixed(dec) : String(Math.round(v)); };
        if (reduce) { el.textContent = raw; return; }
        var start = null;
        requestAnimationFrame(function tick(ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / 1500), eased = 1 - Math.pow(1 - p, 3);
          el.textContent = p < 1 ? fmt(target * eased) : raw;
          if (p < 1) requestAnimationFrame(tick);
        });
      });
    }, { threshold: 0.5 });
    countEls.forEach(function (el) { countIO.observe(el); });
  }

  if (reduce) return; // everything below is decorative motion

  /* ---------- Magnetic primary CTA (desktop, fine pointer) ---------- */
  if (fine && desktop) {
    document.querySelectorAll('.rs-magnetic').forEach(function (btn) {
      var raf = null, RADIUS = 90, PULL = 0.18;
      function onMove(e) {
        var r = btn.getBoundingClientRect();
        var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        var dx = e.clientX - cx, dy = e.clientY - cy;
        var dist = Math.hypot(dx, dy);
        if (dist > RADIUS + Math.max(r.width, r.height) / 2) return reset();
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          btn.classList.add('is-pulling');
          btn.style.setProperty('--rs-mx', (dx * PULL).toFixed(1) + 'px');
          btn.style.setProperty('--rs-my', (dy * PULL).toFixed(1) + 'px');
        });
      }
      function reset() {
        btn.classList.remove('is-pulling');
        btn.style.removeProperty('--rs-mx');
        btn.style.removeProperty('--rs-my');
      }
      window.addEventListener('mousemove', onMove, { passive: true });
      btn.addEventListener('mouseleave', reset);
    });
  }

  /* ---------- Cinematic hero scroll: media parallax + content fade + bloom ----
     Ken-Burns lives on the <img> (CSS); parallax translates the <picture> so the
     two transforms never collide. Bloom has no CSS animation, so JS owns it. */
  var cineHero = document.querySelector('[data-cine-hero]');
  if (cineHero) {
    var cMedia = cineHero.querySelector('.cine-hero__media');
    var cWrap  = document.getElementById('cineHeroWrap');
    var cBloom = document.getElementById('cineHeroBloom');
    var cTick = false;
    function cineScroll() {
      if (cTick) return;
      cTick = true;
      requestAnimationFrame(function () {
        var y = window.scrollY, vh = window.innerHeight || 1;
        if (y < vh * 1.25) {
          if (cMedia) cMedia.style.transform = 'translate3d(0,' + (y * 0.15).toFixed(1) + 'px,0)';
          if (cWrap) {
            cWrap.style.transform = 'translate3d(0,' + (y * 0.28).toFixed(1) + 'px,0)';
            cWrap.style.opacity = Math.max(0, 1 - y / (vh * 0.72)).toFixed(3);
          }
          if (cBloom) {
            var p = Math.min(1, y / (vh * 0.6));
            cBloom.style.opacity = (0.25 + p * 0.7).toFixed(3);
            cBloom.style.transform = 'scale(' + (1 + p * 0.5).toFixed(3) + ')';
          }
        }
        cTick = false;
      });
    }
    window.addEventListener('scroll', cineScroll, { passive: true });
    cineScroll();
  }

  /* ---------- Scroll-driven bloom crossfade ---------- */
  document.querySelectorAll('.rs-bloom').forEach(function (bloom) {
    var ticking = false;
    function update() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var r = bloom.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        // progress 0->1 as the element travels through the viewport
        var p = 1 - (r.top + r.height * 0.5) / (vh + r.height * 0.5);
        p = Math.max(0, Math.min(1, p));
        var step = p < 0.34 ? 0 : (p < 0.67 ? 1 : 2);
        bloom.setAttribute('data-rs-bloom-step', String(step));
        ticking = false;
      });
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  });

  /* ---------- Pinned story: activate frame + step as each step enters view ---------- */
  document.querySelectorAll('[data-rs-story]').forEach(function (story) {
    var frames = story.querySelectorAll('[data-rs-frame]');
    var steps = story.querySelectorAll('[data-rs-step]');
    if (!frames.length || !steps.length) return;
    function activate(i) {
      frames.forEach(function (f, x) { f.classList.toggle('is-on', x === i); });
      steps.forEach(function (s, x) { s.classList.toggle('is-on', x === i); });
    }
    var storyIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) activate(parseInt(entry.target.getAttribute('data-rs-step'), 10) || 0);
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    steps.forEach(function (s) { storyIO.observe(s); });
  });
})();
